/**
 * Which of the latest run's vipupisteet the room sees (#1474, specs/workshop-modules/systemic_map.md
 * v3, "Vipupisteet"), in Ongelmakartta v3's facilitator panel: every vipupiste the run made, by
 * type, the ones that passed first and checked by default, a rejected one with what it failed.
 * When the run had the judge on, each shows how many of its tests it passed and which failed; a
 * rejected one can still be shown. Each checkbox saves at once. One a solution is written on stays shown, its box locked with how
 * many; the server refuses it too.
 */
import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import API_ENDPOINTS from '../../../config/api';
import { useTranslation } from '../../../hooks/useTranslation';
import { v2Json } from '../v3Fetch';

const TYPES = ['konsensus', 'kiistanalainen', 'villi_kortti', 'sokea_piste'];

const Row = styled.label`
  display: grid; grid-template-columns: auto 1fr auto; gap: 10px; align-items: center;
  min-height: 48px; box-sizing: border-box; padding: 8px 10px; border-radius: 8px; border: 1px solid #2c3444;
  font-size: 14px; cursor: pointer;
  opacity: ${(p) => (p.$passed ? 1 : 0.75)};
  input { width: 20px; height: 20px; }
  small { display: block; margin-top: 2px; color: rgba(255,255,255,0.55); font-size: 12px; }
  b { font-variant-numeric: tabular-nums; font-weight: 500; color: rgba(255,255,255,0.65); }
`;
const Type = styled.p`margin: 6px 0 0 !important; font-size: 13px; color: rgba(255,255,255,0.65);`;
const Muted = styled.p`color: rgba(255,255,255,0.55); font-size: 13px;`;

/** @param {{ instanceId: string, onError: (message: string) => void }} props */
export default function PivotChoice({ instanceId, onError }) {
  const { t } = useTranslation();
  const k = (key, vars) => t(`ongelmakarttaV3.admin.choice.${key}`, vars);
  const [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setChoice(await v2Json(API_ENDPOINTS.SYSTEMIC_MAP_V3_PIVOT_CHOICE(instanceId)));
    } catch (err) {
      onError(t('ongelmakarttaV3.admin.loadFailed', { error: err.message }));
    }
  }, [instanceId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  if (!choice) return null;
  if (!choice.runId) return <Muted>{k('noRun')}</Muted>;

  const toggle = async (pivot) => {
    const shown = choice.pivots.filter((p) => p.shown).map((p) => p.id);
    const pivotIds = pivot.shown ? shown.filter((id) => id !== pivot.id) : [...shown, pivot.id];
    setBusy(true);
    try {
      setChoice(await v2Json(API_ENDPOINTS.SYSTEMIC_MAP_V3_PIVOT_CHOICE(instanceId), {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pivotIds }),
      }));
    } catch (err) {
      onError(t('ongelmakarttaV3.admin.failed', { error: err.message }));
      await load();
    } finally {
      setBusy(false);
    }
  };

  const judged = (tests) => {
    const failed = Object.keys(tests).filter((x) => !tests[x]);
    const line = k('judged', { passed: Object.keys(tests).length - failed.length, total: Object.keys(tests).length });
    return failed.length ? `${line}: ${failed.map((x) => k(`tests.${x}`)).join(' · ')}` : line;
  };
  const shownCount = choice.pivots.filter((p) => p.shown).length;
  return (
    <>
      <Muted>{k('lead', { shown: shownCount, total: choice.pivots.length })}</Muted>
      {TYPES.map((type) => {
        const list = choice.pivots.filter((p) => p.type === type);
        if (!list.length) return null;
        return (
          <React.Fragment key={type}>
            <Type>{t(`ongelmakarttaV3.pivot.types.${type}`)}</Type>
            {list.map((p) => {
              const held = p.shown && p.solutions > 0;
              return (
                <Row key={p.id} $passed={p.passed}>
                  <input type="checkbox" checked={p.shown} disabled={busy || held} onChange={() => toggle(p)} />
                  <span>
                    {p.title}
                    {p.tests ? <small>{judged(p.tests)}</small> : null}
                    {!p.passed ? <small>{k('failed', { list: p.failedValidators.map((v) => k(`validators.${v}`)).join(' · ') })}</small> : null}
                    {held ? <small>{k('held', { count: p.solutions })}</small> : null}
                  </span>
                  <b>{Number.isFinite(p.score) ? p.score.toFixed(2) : ''}</b>
                </Row>
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
}
